import clsx from 'clsx'
import {FC, FormEvent, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useQuery} from 'react-query'
import {Link} from 'react-router-dom'
import {AmountInput, AmountWords} from '../../apps/shared/amount/AmountInput'
import {businessSettingsPath} from '../core/businessSettingsPath'
import {goToGateway, readApiError, rial} from '../core/onlinePaymentApi'
import {hidesPayments} from '../../install-app/core/androidApp'
import {StoreBuildUnavailableDialog, StoreBuildUnavailableNote} from '../components/StoreBuildUnavailable'
import {GatewayPicker} from '../components/GatewayPicker'
import {NOTICE_DELAY_MS} from '../components/OnlinePaymentDialog'
import {
  getChargeQuote,
  getPersonalChargeQuote,
  getPersonalWallet,
  getWallet,
  personalWalletKey,
  startPersonalWalletCharge,
  startWalletCharge,
  walletKey,
  WalletSummary,
} from '../core/walletApi'
import {amountProblem} from './walletCharge'
import {currentReturnPath, rememberWalletResume} from './walletResume'
import './WalletPanel.scss'

// «افزایش موجودی و ادامه» (user request 2026-10-17): wherever a step is short of wallet credit, the shortfall is
// offered as the charge (or more, as the payer chooses), the payer goes straight to the gateway, comes back to the
// same page, and the step goes on by itself once the credit is in (walletResume, WalletReturnHost).

/** The step to go on with after the charge; without it the payer comes back and repeats the step by hand. */
export type WalletResumeStep = {
  action: string
  payload?: unknown
  /** The step in the user's words: «فعال‌سازی پیش‌فاکتور». */
  label: string
}

/** What a step lacks: required minus balance, never below zero. */
export const shortfallOf = (balance?: number | null, required?: number | null): number =>
  balance === undefined || balance === null || required === undefined || required === null
    ? 0
    : Math.max(0, Math.ceil(required - balance))

/** The charge offered first: the shortfall, at least the wallet's minimum charge, at most its maximum. */
export const firstChargeOf = (shortfall: number, minimum: number, maximum: number): number => {
  const first = Math.max(shortfall, minimum)
  return maximum > 0 ? Math.min(first, maximum) : first
}

/** The charges offered as tiles: the shortfall first, then the wallet's suggested amounts above it (three at most). */
export const chargeChoicesOf = (
  shortfall: number,
  minimum: number,
  maximum: number,
  suggested: number[]
): number[] => {
  const first = firstChargeOf(shortfall, minimum, maximum)
  const more = suggested
    .filter((value) => value > first && (maximum <= 0 || value <= maximum))
    .slice(0, 3)
  return [first, ...more]
}

/**
 * The charge window: what the step needs, the shortfall offered as the charge (or a larger amount), its VAT as the
 * server quotes it, then the gateway. The step is remembered first, so it goes on after the payment. With `personal`
 * it charges the signed-in person's own wallet (1405/07/30: «کیف پول شخصی») the same way, invoiced in their name.
 */
type WalletTopUpProps = {
  /** The business whose wallet is charged; ignored with `personal`. */
  companyId: number
  /** The signed-in person's own wallet instead of a business's (backend 1.0.0.723). */
  personal?: boolean
  shortfall: number
  /** «فعال‌سازی», «ادامه»: what the payer is doing, for the title and the pay button. */
  actionLabel: string
  resume?: WalletResumeStep
  onClose: () => void
}

/** In the Bazaar and Myket builds of the Android app nothing is charged: the window only says so (hidesPayments). */
export const WalletTopUpDialog: FC<WalletTopUpProps> = (props) =>
  hidesPayments() ? <StoreBuildUnavailableDialog onClose={props.onClose} /> : <WalletTopUpWindow {...props} />

const WalletTopUpWindow: FC<WalletTopUpProps> = ({companyId, personal = false, shortfall, actionLabel, resume, onClose}) => {
  const wallet = useQuery<Partial<WalletSummary>>(
    personal ? personalWalletKey : walletKey(companyId),
    () => (personal ? getPersonalWallet() : getWallet(companyId)),
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  )
  const minimum = wallet.data?.minimumCharge || 0
  const maximum = wallet.data?.maximumCharge || 0
  const need = firstChargeOf(shortfall, minimum, maximum)
  const [text, setText] = useState<string>()
  const amount = text === undefined ? need : Number(text || 0)
  const [debounced, setDebounced] = useState(amount)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  // The organisation's gateway the payer picked (2026-10-21); the first, its default, until then.
  const gateways = wallet.data?.gateways || []
  const [picked, setPicked] = useState<number>()
  const gatewayId = gateways.some((item) => item.id === picked) ? picked : gateways[0]?.id

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(amount), 350)
    return () => window.clearTimeout(timer)
  }, [amount])

  const problem = !wallet.data
    ? ''
    : amount < need
    ? `برای ادامه دست‌کم ${rial(need)} ریال لازم است.`
    : amountProblem(amount, minimum, maximum)
  const quote = useQuery(
    ['wallet-quote', personal ? 'personal' : companyId, debounced],
    () => (personal ? getPersonalChargeQuote(debounced) : getChargeQuote(companyId, debounced)),
    {
      enabled:
        Boolean(wallet.data?.chargeEnabled && wallet.data?.canCharge) && debounced > 0 && !problem,
      retry: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  )
  const current = !problem && quote.data && quote.data.credit === amount ? quote.data : undefined
  const quoting = !problem && !current && (quote.isFetching || debounced !== amount)
  const choices = wallet.data
    ? chargeChoicesOf(shortfall, minimum, maximum, wallet.data.suggestedAmounts || [])
    : []
  const closed =
    wallet.data && (!wallet.data.chargeEnabled || !wallet.data.canCharge || !wallet.data.available)

  const pay = async (event: FormEvent) => {
    event.preventDefault()
    // The dialog is a portal, but React passes its submit on to a form it sits in (a settings page's): that form must
    // never save because the payer paid here.
    event.stopPropagation()
    if (busy || problem || !amount) return
    setBusy(true)
    setError('')
    const returnPath = currentReturnPath()
    try {
      // A business's step goes on by itself after the charge; a person repeats theirs (one press of «استعلام»).
      if (resume && !personal) {
        rememberWalletResume({
          companyId,
          action: resume.action,
          payload: resume.payload,
          label: resume.label,
          returnPath,
        })
      }
      const started = personal
        ? await startPersonalWalletCharge(amount, returnPath, gatewayId)
        : await startWalletCharge(companyId, amount, undefined, returnPath, gatewayId)
      if (started.notice) {
        // The picked gateway refused and another took the charge: said before the bank's page opens.
        setNotice(started.notice)
        window.setTimeout(() => goToGateway(started.redirect), NOTICE_DELAY_MS)
      } else {
        goToGateway(started.redirect)
      }
    } catch (chargeError) {
      setError(
        readApiError(chargeError, 'اتصال به درگاه پرداخت انجام نشد؛ دوباره تلاش کنید.').message
      )
      setBusy(false)
    }
  }

  return (
    <Modal
      show
      centered
      fullscreen='sm-down'
      onHide={busy ? () => undefined : onClose}
      aria-labelledby='wtu-title'
    >
      <form onSubmit={pay} noValidate>
        <Modal.Header closeButton={!busy} closeLabel='بستن'>
          <Modal.Title id='wtu-title' as='h2' className='fs-4'>
            افزایش موجودی و {actionLabel}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {wallet.isLoading ? (
            <div className='text-muted py-6 text-center'>در حال دریافت کیف پول…</div>
          ) : wallet.isError || !wallet.data ? (
            <div className='alert alert-danger d-flex align-items-center gap-3 mb-0' role='alert'>
              <span className='flex-grow-1'>
                {readApiError(wallet.error, 'کیف پول دریافت نشد.').message}
              </span>
              <button
                type='button'
                className='btn btn-sm btn-light'
                onClick={() => wallet.refetch()}
              >
                تلاش مجدد
              </button>
            </div>
          ) : closed ? (
            personal ? (
              <div className='alert alert-light-warning mb-0'>
                شارژ آنلاین کیف پول شخصی فعلاً در دسترس نیست؛ با پشتیبانی معتمد آسا تماس بگیرید.
              </div>
            ) : (
              <div className='alert alert-light-warning mb-0'>
                شارژ آنلاین کیف پول برای این حساب در دسترس نیست
                {wallet.data.canCharge === false
                  ? ' (در جلسهٔ پشتیبانی یا بدون دسترسی به کیف پول)'
                  : ''}
                ؛ از مدیر کسب‌وکار بخواهید کیف پول را شارژ کند.{' '}
                <Link to={businessSettingsPath(companyId, 'wallet')} onClick={onClose}>
                  کیف پول
                </Link>
              </div>
            )
          ) : (
            <>
              <div className='wlt-muted mb-4'>
                {shortfall > 0 ? (
                  <>
                    موجودی کیف پول{personal ? ' شخصی' : ''}{' '}
                    <strong>{rial(wallet.data.balance ?? 0)} ریال</strong> است و برای {actionLabel}{' '}
                    <strong>{rial(shortfall)} ریال</strong> کم دارد.
                  </>
                ) : (
                  <>
                    موجودی کیف پول{personal ? ' شخصی' : ''} {rial(wallet.data.balance ?? 0)} ریال
                    است.
                  </>
                )}
                {minimum > shortfall && shortfall > 0
                  ? ` کمترین مبلغ شارژ ${rial(minimum)} ریال است.`
                  : ''}
              </div>

              <div className='wlt-presets' role='group' aria-label='مبلغ شارژ'>
                {choices.map((value, index) => (
                  <button
                    key={value}
                    type='button'
                    className={clsx('wlt-preset', amount === value && 'is-selected')}
                    aria-pressed={amount === value}
                    onClick={() => {
                      setError('')
                      setText(String(value))
                    }}
                  >
                    <span className='wlt-preset__amount'>
                      {rial(value)} <small>ریال</small>
                    </span>
                    <span className='wlt-preset__hint'>
                      {index === 0
                        ? shortfall > 0 && shortfall >= minimum
                          ? 'به اندازهٔ کسری'
                          : 'کمترین مبلغ شارژ'
                        : 'مبلغ بیشتر'}
                    </span>
                    <i className='las la-check-circle wlt-preset__check' aria-hidden='true' />
                  </button>
                ))}
              </div>

              <label className='form-label mt-4' htmlFor='wtu-amount'>
                مبلغ دلخواه
              </label>
              <span className='input-group'>
                <AmountInput
                  id='wtu-amount'
                  className={clsx('form-control', problem && 'is-invalid')}
                  aria-label='مبلغ شارژ به ریال'
                  aria-describedby='wtu-amount-words wtu-amount-problem'
                  aria-invalid={Boolean(problem)}
                  value={text === undefined ? String(need) : text}
                  onValueChange={(digits) => {
                    setError('')
                    setText(digits)
                  }}
                />
                <span className='input-group-text'>ریال</span>
              </span>
              <AmountWords id='wtu-amount-words' value={text === undefined ? String(need) : text} />
              {problem && (
                <div id='wtu-amount-problem' className='text-danger fs-7 mt-1'>
                  {problem}
                </div>
              )}

              {!problem && (
                <div className='wlt-summary mt-4' aria-live='polite' aria-busy={quoting}>
                  <dl className='wlt-summary__rows my-0'>
                    <div>
                      <dt>اعتبار کیف پول</dt>
                      <dd>{rial(amount)} ریال</dd>
                    </div>
                    <div>
                      <dt>
                        مالیات بر ارزش افزوده
                        {current ? ` (${current.vatRate.toLocaleString('fa-IR')}٪)` : ''}
                      </dt>
                      <dd>
                        {current ? (
                          `${rial(current.vat)} ریال`
                        ) : quoting ? (
                          <span className='wlt-skeleton' />
                        ) : (
                          '—'
                        )}
                      </dd>
                    </div>
                    <div className='wlt-summary__total'>
                      <dt>مبلغ قابل پرداخت</dt>
                      <dd>
                        {current ? (
                          `${rial(current.payable)} ریال`
                        ) : quoting ? (
                          <span className='wlt-skeleton' />
                        ) : (
                          '—'
                        )}
                      </dd>
                    </div>
                  </dl>
                  {quote.isError && !quoting && (
                    <div className='wlt-hint is-error mt-3'>
                      محاسبهٔ مالیات انجام نشد؛ مبلغ نهایی در درگاه نمایش داده می‌شود.
                    </div>
                  )}
                </div>
              )}

              {gateways.length > 1 && (
                <GatewayPicker
                  className='mt-4'
                  name='wtu-gateway'
                  gateways={gateways}
                  value={gatewayId}
                  onChange={setPicked}
                  disabled={busy}
                  fallbackNote='اگر درگاه انتخاب‌شده در دسترس نباشد، پرداخت با درگاه بعدی ادامه می‌یابد.'
                />
              )}

              <p className='text-muted fs-8 mt-4 mb-0'>
                {personal
                  ? `پس از پرداخت به همین صفحه برمی‌گردید و صورتحساب شارژ به نام شما صادر می‌شود؛ سپس ${actionLabel} را دوباره بزنید.`
                  : resume
                  ? `پس از پرداخت به همین صفحه برمی‌گردید و «${resume.label}» خودکار ادامه پیدا می‌کند.`
                  : `پس از پرداخت به همین صفحه برمی‌گردید؛ سپس ${actionLabel} را دوباره بزنید.`}
              </p>
              {notice && (
                <div className='alert alert-light-warning py-2 mt-3 mb-0' role='status'>
                  {notice}
                </div>
              )}
              {error && (
                <div className='alert alert-danger py-2 mt-3 mb-0' role='alert'>
                  {error}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={busy}>
            انصراف
          </button>
          {!closed && wallet.data && (
            <button
              type='submit'
              className='btn btn-primary'
              disabled={busy || Boolean(problem) || quoting}
            >
              {busy && (
                <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>
              )}
              {busy
                ? 'در حال انتقال به درگاه…'
                : current
                ? `پرداخت ${rial(current.payable)} ریال و ${actionLabel}`
                : `پرداخت و ${actionLabel}`}
            </button>
          )}
        </Modal.Footer>
      </form>
    </Modal>
  )
}

/**
 * Where a step stopped for want of credit: the shortfall and «افزایش موجودی و …», which opens the charge window. With
 * the balance and the amount the step needs unknown, the window starts from the wallet's minimum charge.
 */
type WalletShortfallProps = {
  companyId: number
  balance?: number | null
  required?: number | null
  /** «فعال‌سازی», «ادامه», «ذخیره»: the step. */
  actionLabel?: string
  resume?: WalletResumeStep
  className?: string
}

/** In the Bazaar and Myket builds of the Android app there is no charge to offer (hidesPayments). */
export const WalletShortfallAction: FC<WalletShortfallProps> = (props) =>
  hidesPayments() ? <StoreBuildUnavailableNote className={props.className} /> : <WalletShortfallButton {...props} />

const WalletShortfallButton: FC<WalletShortfallProps> = ({companyId, balance, required, actionLabel = 'ادامه', resume, className}) => {
  const [open, setOpen] = useState(false)
  const shortfall = shortfallOf(balance, required)
  return (
    <span className={clsx('d-inline-flex flex-wrap align-items-center gap-2', className)}>
      {shortfall > 0 && <span className='fw-semibold'>کسری موجودی: {rial(shortfall)} ریال</span>}
      <button type='button' className='btn btn-sm btn-primary' onClick={() => setOpen(true)}>
        <i className='las la-wallet fs-4 me-1' aria-hidden='true'></i>
        افزایش موجودی و {actionLabel}
      </button>
      {open && (
        <WalletTopUpDialog
          companyId={companyId}
          shortfall={shortfall}
          actionLabel={actionLabel}
          resume={resume}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  )
}
